// frontend/src/features/ogdb2/pages/BAIDDetailPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { OGDB2 } from "../ogdb2Api";
import { OGDB2_METRICS, OGDB2_METRIC_CATEGORIES } from "../metricRegistry";
import { computeCapitolDCF } from "../utils/capitolDcf";
import ProductionDetailModal from "../components/ProductionDetailModal";

const TABS = [
  { key: "facts", label: "Facts" },
  { key: "levers", label: "Levers (DCF)" },
  { key: "other", label: "Other" },
];

const SCENARIOS_BASE = [
  { key: "assessor", label: "Assessor" },
  { key: "actual", label: "Actual" },
  { key: "third_party", label: "3rd Party" },
  { key: "ba", label: "BA" },
];

function toNum(x) {
  if (x === null || x === undefined) return null;
  const s = String(x).trim();
  if (!s) return null;
  const n = Number(s.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function fmtMoney0(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  const v = Number(n);
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function fmtPct2(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return `${Number(n).toFixed(2)}%`;
}

export default function BAIDDetailPage({ email, unitId, initialUnit = null, initialTaxYear = null, onBack }) {
  const [tab, setTab] = useState("facts");
  const [error, setError] = useState("");
  const [showHiddenMetrics, setShowHiddenMetrics] = useState(false);
  const [showThirdParty, setShowThirdParty] = useState(true);

  const [unit, setUnit] = useState(initialUnit);
  const [taxYear, setTaxYear] = useState(
    initialTaxYear !== null && initialTaxYear !== undefined && String(initialTaxYear).trim() !== ""
      ? String(initialTaxYear).trim()
      : String(new Date().getFullYear())
  );

  const [factsPayload, setFactsPayload] = useState(null);
  
  const [prodModalOpen, setProdModalOpen] = useState(false);

  const resolved = useMemo(() => factsPayload?.resolved || {}, [factsPayload]);

  const factsList = useMemo(() => {
    const arr = factsPayload?.facts;
    return Array.isArray(arr) ? arr : [];
  }, [factsPayload]);

  // Build registry buckets
  const registryByCategory = useMemo(() => {
    const cats = {
      facts_quick: [],
      production: [],
      expenses: [],
      price_deck: [],
      ownership: [],
      valuation: [],
      other: [],
    };

    for (const m of OGDB2_METRICS || []) {
      if (!m?.code) continue;
      const c = m.categories || [];

      if (c.includes(OGDB2_METRIC_CATEGORIES.PRODUCTION)) cats.production.push(m);
      else if (c.includes(OGDB2_METRIC_CATEGORIES.EXPENSES)) cats.expenses.push(m);
      else if (c.includes(OGDB2_METRIC_CATEGORIES.PRICE_DECK)) cats.price_deck.push(m);
      else if (c.includes(OGDB2_METRIC_CATEGORIES.OWNERSHIP)) cats.ownership.push(m);
      else if (c.includes(OGDB2_METRIC_CATEGORIES.VALUATION)) cats.valuation.push(m);
      else if (c.includes(OGDB2_METRIC_CATEGORIES.FACTS_QUICK)) cats.facts_quick.push(m);
      else cats.other.push(m);
    }

    return cats;
  }, []);

  useEffect(() => {
    if (!unitId) return;
    if (initialTaxYear === null || initialTaxYear === undefined) return;
    const s = String(initialTaxYear).trim();
    if (!s) return;
    setTaxYear(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId, initialTaxYear]);

  useEffect(() => {
    if (!unitId) return;
    setError("");
    OGDB2.getBAIDUnitDetail(unitId)
      .then((res) => setUnit(res?.data?.unit || null))
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed to load BAID unit"));
  }, [unitId]);

  useEffect(() => {
    if (!unitId) return;
    setError("");
    const ty = Number.isFinite(Number(taxYear)) ? Number(taxYear) : null;

    OGDB2.listBAIDFacts(unitId, ty)
      .then((res) => setFactsPayload(res?.data || null))
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed to load BAID facts"));
  }, [unitId, taxYear]);

  async function upsertBaidFact(scenario, metric_code, rawValue, metricDef) {
    if (!unitId) return;
    setError("");

    const type = metricDef?.type || "number";
    let value_num = null;
    let value_text = null;

    if (type === "text") {
      value_text = rawValue === null || rawValue === undefined ? "" : String(rawValue);
    } else {
      const n =
        rawValue === null || rawValue === undefined || String(rawValue).trim() === ""
          ? null
          : Number(String(rawValue).replace(/,/g, ""));
      value_num = Number.isFinite(n) ? n : null;
      if (!Number.isFinite(n) && String(rawValue || "").trim() !== "") {
        value_text = String(rawValue);
      }
    }

    const ty = Number.isFinite(Number(taxYear)) ? Number(taxYear) : null;

    try {
      await OGDB2.upsertBAIDFact(unitId, {
        tax_year: ty,
        scenario,
        metric_code,
        value_num,
        value_text,
        unit: metricDef?.unit || null,
        updated_by: email || null,
      });

      const res = await OGDB2.listBAIDFacts(unitId, ty);
      setFactsPayload(res?.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to save value");
    }
  }

  // Helper to read a value for a metric+scenario.
  function getMetricValue(metricDef, scenarioKey) {
    const code = metricDef?.code;
    if (!code) return "";

    // LOE Basis is always tied to assessor for Actual + BA
    if (code === "loe_basis" && (scenarioKey === "actual" || scenarioKey === "ba")) {
      scenarioKey = "assessor";
    }
	
	// ✅ Derived metric: Comparable LOE
    if (code === "loe_comparable") {
      return String(getComparableLOE(scenarioKey));
    }
	
	// ✅ 3P comparable start rates render inside the comparable_* start rate rows (3P column only)
    if (code === "comparable_oil_start_rate" && scenarioKey === "third_party") {
      return String(getComparable3PStartRate("oil"));
    }

    if (code === "comparable_gas_start_rate" && scenarioKey === "third_party") {
      return String(getComparable3PStartRate("gas"));
    }

    const type = metricDef?.type || "number";

    if (type === "text") {
      const f = factsList.find((x) => x?.metric_code === code && x?.scenario === scenarioKey);
      const vt = f?.value_text;
      const vn = f?.value_num;
      if (vt !== null && vt !== undefined && String(vt).trim() !== "") return String(vt);
      if (vn !== null && vn !== undefined && String(vn).trim() !== "") return String(vn);
      return "";
    }

    const row = resolved?.[code] || {};
    const v = row?.[scenarioKey];
    return v === null || v === undefined ? "" : String(v);
  }

  function getTextMetric(code) {
    const def = { code, type: "text" };
    return (
      getMetricValue(def, "ba") ||
      getMetricValue(def, "assessor") ||
      getMetricValue(def, "actual") ||
      "—"
    );
  }

  // Comparable LOE logic (aligned with your current rules)
  function getComparableLOE(scenarioKey) {
    const assessorImported = toNum(getMetricValue({ code: "loe", type: "money" }, "assessor"));

    if (scenarioKey === "assessor") return assessorImported ?? 0;

    if (scenarioKey === "ba") {
      // BA override stored in factsList as metric_code loe_comparable scenario ba
      const overrideFact = factsList.find((x) => x?.metric_code === "loe_comparable" && x?.scenario === "ba");
      const o = toNum(overrideFact?.value_num ?? overrideFact?.value_text);
      return o !== null ? o : (assessorImported ?? 0);
    }

    // Actual: bucket logic (OpEx/Marketing) using assessor basis
    const basis = String(getMetricValue({ code: "loe_basis", type: "text" }, "assessor") || "").trim();
    const opex = toNum(getMetricValue({ code: "opex", type: "money" }, "actual")) || 0;
    const mkt = toNum(getMetricValue({ code: "marketing", type: "money" }, "actual")) || 0;
    const isNet = basis === "net_of_marketing";
    return isNet ? opex : (opex + mkt);
  }
  
  function getComparable3PStartRate(product) {
    const basisCode = product === "oil" ? "tp_start_rate_basis_oil" : "tp_start_rate_basis_gas";

    // BA override wins (saved via Bulk Analysis), else third_party default, else sep_dec
    const basis =
      String(getMetricValue({ code: basisCode, type: "text" }, "ba") || "").trim() ||
      String(getMetricValue({ code: basisCode, type: "text" }, "third_party") || "").trim() ||
      "sep_dec";

    const map = product === "oil"
      ? {
          sep_dec: "tp_oil_sr_sep_dec",
          last_1: "tp_oil_sr_last_1",
          last_3: "tp_oil_sr_last_3",
          last_4: "tp_oil_sr_last_4",
          last_6: "tp_oil_sr_last_6",
          last_12: "tp_oil_sr_last_12",
        }
      : {
          sep_dec: "tp_gas_sr_sep_dec",
          last_1: "tp_gas_sr_last_1",
          last_3: "tp_gas_sr_last_3",
          last_4: "tp_gas_sr_last_4",
          last_6: "tp_gas_sr_last_6",
          last_12: "tp_gas_sr_last_12",
        };

    function pick(code) {
      const v = toNum(getMetricValue({ code, type: "number" }, "third_party"));
      return v !== null ? v : null;
    }

    const primary = map[basis] || map.sep_dec;
    return (
      pick(primary) ??
      pick(map.sep_dec) ??
      pick(map.last_4) ??
      pick(map.last_3) ??
      pick(map.last_1) ??
      0
    );
  }

  // DCF Preview for Assessor + BA
  const dcfAssessor = useMemo(() => {
    try {
      return computeCapitolDCF({
        scenarioKey: "assessor",
        getMetricValue,
        getComparableLOE,
      });
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factsPayload]);

  const dcfBA = useMemo(() => {
    return computeCapitolDCF({
      scenarioKey: "ba",
      getMetricValue,
      getComparableLOE: (scenario) => getComparableLOE(scenario),
    });
  }, [factsPayload, factsList]);

  if (!unitId) return null;

  const headerTitle = unit?.display_name || unit?.well_name || unit?.lease_name || `BAID ${unitId}`;
  const appraisalDistrict = getTextMetric("appraisal_district");

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {String(error)}
        </div>
      )}

      {/* Header */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm text-gray-500">BAID</div>
            <div className="text-lg font-semibold text-gray-900 truncate">{headerTitle}</div>
            <div className="text-xs text-gray-500 mt-1">
              BAID #{unitId}
              {" · "}
              Client: {unit?.client || "—"}
              {" · "}
              Operator: {unit?.operator || "—"}
              {" · "}
              Appraisal District: {appraisalDistrict}
              {" · "}
              Appraisal Firm: {unit?.appraisal_firm || "—"}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Tax Year</span>
              <input
                className="w-28 rounded-xl border border-gray-200 px-3 py-2"
                value={taxYear}
                onChange={(e) => setTaxYear(e.target.value)}
              />
            </div>

            <button className="btn" onClick={onBack} type="button">
              Back
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="rounded-2xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-4 py-2 flex items-center gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={tab === t.key ? "btn-primary" : "btn"}
              onClick={() => {
                if (document.activeElement && typeof document.activeElement.blur === "function") {
                  document.activeElement.blur();
                }
                setTimeout(() => setTab(t.key), 0);
              }}
              type="button"
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-4 space-y-4">
          {tab === "facts" && <FactsTab unit={unit} getMetricValue={getMetricValue} />}

          {tab === "levers" && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-gray-200 bg-white p-3">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={showHiddenMetrics}
                    onChange={(e) => setShowHiddenMetrics(e.target.checked)}
                  />
                  Show hidden / blank metrics
                </label>
				<label className="flex items-center gap-2 text-sm text-gray-700 mt-2">
				  <input
					type="checkbox"
					checked={showThirdParty}
					onChange={(e) => setShowThirdParty(e.target.checked)}
				  />
				  Show 3rd Party (GasSearch)
				</label>
				
                <div className="text-xs text-gray-500 mt-1">
                  When off, rows where Assessor/Actual/BA are all blank are hidden.
                </div>
              </div>

              <DCFPreview
                assessor={dcfAssessor}
                ba={dcfBA}
                importedAssessorWI={toNum(getMetricValue({ code: "working_interest_value", type: "money" }, "assessor"))}
                importedAssessorRI={toNum(getMetricValue({ code: "royalty_interest_value", type: "money" }, "assessor"))}
              />

              <CategoryEditor
                title="Ownership (Key Splits)"
                showHiddenMetrics={showHiddenMetrics}
                description="These splits drive the Working vs Royalty math. Usually not negotiated, but required for apples-to-apples."
                metrics={registryByCategory.ownership}
                onSave={upsertBaidFact}
                getMetricValue={getMetricValue}
				showThirdParty={showThirdParty}
              />
			  
			  <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-gray-900">Production (Forecast Drivers)</div>
                <button className="btn" type="button" onClick={() => setProdModalOpen(true)}>
                  Production Detail (Full Screen)
                </button>
              </div>

              <CategoryEditor
                title="Production (Forecast Drivers)"
                showHiddenMetrics={showHiddenMetrics}
                description="Start rates + decline rates drive the forecast."
                metrics={registryByCategory.production}
                onSave={upsertBaidFact}
                getMetricValue={getMetricValue}
                getComparable3PStartRate={getComparable3PStartRate}
				showThirdParty={showThirdParty}
              />

              <CategoryEditor
                title="Price Deck"
                showHiddenMetrics={showHiddenMetrics}
                description="Oil & gas prices drive revenue."
                metrics={registryByCategory.price_deck}
                onSave={upsertBaidFact}
                getMetricValue={getMetricValue}
				showThirdParty={showThirdParty}
              />

              <CategoryEditor
                title="Expenses"
                showHiddenMetrics={showHiddenMetrics}
                description="Comparable LOE is the negotiated number that must match the lease summary logic."
                metrics={registryByCategory.expenses}
                onSave={upsertBaidFact}
                getMetricValue={getMetricValue}
				showThirdParty={showThirdParty}
              />

              <CategoryEditor
                title="Valuation Assumptions"
                showHiddenMetrics={showHiddenMetrics}
                description="Discount rate, economic life, equipment value, and LOE behavior."
                metrics={registryByCategory.valuation}
                onSave={upsertBaidFact}
                getMetricValue={getMetricValue}
				showThirdParty={showThirdParty}
              />
            </div>
          )}

          {tab === "other" && (
            <div className="space-y-4">
              <CategoryEditor
                title="Quick Facts"
                showHiddenMetrics={showHiddenMetrics}
                description="Canonical identifiers + metadata from the lease summary."
                metrics={registryByCategory.facts_quick}
                onSave={upsertBaidFact}
                getMetricValue={getMetricValue}
              />

              <CategoryEditor
                title="Other"
                showHiddenMetrics={showHiddenMetrics}
                description="Everything else canonicalized from imports."
                metrics={registryByCategory.other}
                onSave={upsertBaidFact}
                getMetricValue={getMetricValue}
				showThirdParty={showThirdParty}
              />
            </div>
          )}
        </div>
      </div>
	  
	  <ProductionDetailModal
        open={prodModalOpen}
        onClose={() => setProdModalOpen(false)}
        unitId={unitId}
        prodYear={Number(taxYear)}
		resolvedFacts={resolved}
        oilBasis={
          String(getMetricValue({ code: "tp_start_rate_basis_oil", type: "text" }, "ba") || "").trim() ||
          String(getMetricValue({ code: "tp_start_rate_basis_oil", type: "text" }, "third_party") || "").trim() ||
          "sep_dec"
        }
        gasBasis={
          String(getMetricValue({ code: "tp_start_rate_basis_gas", type: "text" }, "ba") || "").trim() ||
          String(getMetricValue({ code: "tp_start_rate_basis_gas", type: "text" }, "third_party") || "").trim() ||
          "sep_dec"
        }
        onSetOilBasis={async (v) => {
          await upsertBaidFact("ba", "tp_start_rate_basis_oil", v, {
            code: "tp_start_rate_basis_oil",
            type: "text",
          });
        }}
        onSetGasBasis={async (v) => {
          await upsertBaidFact("ba", "tp_start_rate_basis_gas", v, {
            code: "tp_start_rate_basis_gas",
            type: "text",
          });
        }}
      />

      <style>{`
        .btn { border:1px solid #e5e7eb; background:#fff; padding:8px 10px; border-radius:12px; font-size:14px; }
        .btn:hover { border-color:#d1d5db; }
        .btn-primary { border:1px solid #93c5fd; background:#eff6ff; color:#1d4ed8; padding:8px 10px; border-radius:12px; font-size:14px; }
        .btn-primary:hover { border-color:#60a5fa; }
      `}</style>
    </div>
  );
}

function DCFPreview({ assessor, ba, importedAssessorWI, importedAssessorRI }) {
  const aWI = assessor?.computedWorkingValue ?? null;
  const aRI = assessor?.computedRoyaltyValue ?? null;
  const aTotal = assessor?.totalLeaseValue ?? null;

  const bWI = ba?.computedWorkingValue ?? null;
  const bRI = ba?.computedRoyaltyValue ?? null;
  const bTotal = ba?.totalLeaseValue ?? null;

  const dWI = bWI !== null && aWI !== null ? bWI - aWI : null;
  const dTotal = bTotal !== null && aTotal !== null ? bTotal - aTotal : null;

  return (
    <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
      <div className="text-sm font-semibold text-blue-900">Capitol DCF Preview (Apples-to-Apples)</div>
      <div className="text-xs text-blue-700 mt-1">
        Forecast: start rate + exponential decline · Discount: mid-year · Working floor at Net Equipment Value
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm mt-3">
        <div className="rounded-xl border border-blue-100 bg-white p-3">
          <div className="text-xs text-gray-500">Assessor (Computed)</div>
          <div className="mt-1">
            <div>
              WI: <b>${fmtMoney0(aWI)}</b>
            </div>
            <div>
              RI: <b>${fmtMoney0(aRI)}</b>
            </div>
            <div>
              Total: <b>${fmtMoney0(aTotal)}</b>
            </div>
          </div>
          {assessor?.floorApplied && (
            <div className="text-xs text-amber-700 mt-2">WI floor applied (Net Equipment Value).</div>
          )}
        </div>

        <div className="rounded-xl border border-blue-100 bg-white p-3">
          <div className="text-xs text-gray-500">BA Ask (Computed)</div>
          <div className="mt-1">
            <div>
              WI: <b>${fmtMoney0(bWI)}</b>
            </div>
            <div>
              RI: <b>${fmtMoney0(bRI)}</b>
            </div>
            <div>
              Total: <b>${fmtMoney0(bTotal)}</b>
            </div>
          </div>
          {ba?.floorApplied && (
            <div className="text-xs text-amber-700 mt-2">WI floor applied (Net Equipment Value).</div>
          )}
        </div>

        <div className="rounded-xl border border-blue-100 bg-white p-3">
          <div className="text-xs text-gray-500">Delta (BA − Assessor)</div>
          <div className="mt-1">
            <div>
              WI Δ: <b>${fmtMoney0(dWI)}</b>
            </div>
            <div>
              Total Δ: <b>${fmtMoney0(dTotal)}</b>
            </div>
          </div>

          <div className="text-xs text-gray-500 mt-3">Lease Summary (Imported)</div>
          <div className="text-xs text-gray-700 mt-1">
            WI (imported): <b>${fmtMoney0(importedAssessorWI)}</b>
            <br />
            RI (imported): <b>${fmtMoney0(importedAssessorRI)}</b>
          </div>
        </div>
      </div>
    </div>
  );
}

function FactsTab({ unit, getMetricValue }) {
  // Helper: format a money metric across scenarios
  function MoneyTriplet({ label, code }) {
    const ass = toNum(getMetricValue({ code, type: "money" }, "assessor"));
    const act = toNum(getMetricValue({ code, type: "money" }, "actual"));
    const ba = toNum(getMetricValue({ code, type: "money" }, "ba"));

    return (
      <div className="rounded-xl border border-gray-100 bg-white p-3">
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-gray-900 mt-1">
          BA: <b>${fmtMoney0(ba)}</b>
        </div>
        <div className="text-xs text-gray-500 mt-1">
          Assessor ${fmtMoney0(ass)} · Actual ${fmtMoney0(act)}
        </div>
      </div>
    );
  }

  function NumTriplet({ label, code, unitLabel = "" }) {
    const ass = toNum(getMetricValue({ code, type: "number" }, "assessor"));
    const act = toNum(getMetricValue({ code, type: "number" }, "actual"));
    const ba = toNum(getMetricValue({ code, type: "number" }, "ba"));

    return (
      <div className="rounded-xl border border-gray-100 bg-white p-3">
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-gray-900 mt-1">
          BA: <b>{ba === null ? "—" : ba.toLocaleString()}</b>
          {unitLabel ? <span className="text-gray-500"> {unitLabel}</span> : null}
        </div>
        <div className="text-xs text-gray-500 mt-1">
          Assessor {ass === null ? "—" : ass.toLocaleString()} · Actual {act === null ? "—" : act.toLocaleString()}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="text-sm font-semibold text-gray-900 mb-2">BAID Identity</div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <KV k="RRC / Lease #" v={unit?.rrc_number || unit?.external_account_id || "—"} />
          <KV k="Well Name" v={unit?.well_name || "—"} />
          <KV k="Lease Name" v={unit?.lease_name || "—"} />
          <KV k="Operator" v={unit?.operator || "—"} />
          <KV
            k="Appraisal District"
            v={
              getMetricValue({ code: "appraisal_district", type: "text" }, "ba") ||
              getMetricValue({ code: "appraisal_district", type: "text" }, "assessor") ||
              "—"
            }
          />
          <KV k="Appraisal Firm" v={unit?.appraisal_firm || "—"} />

          <KV k="WI %" v={fmtPct2(unit?.working_interest_pct)} />
          <KV k="RI %" v={fmtPct2(unit?.royalty_interest_pct)} />
          <KV k="Tax Year" v={getMetricValue({ code: "appraisal_year", type: "number" }, "assessor") || "—"} />
        </div>
      </div>

      {/* ✅ NEW: Quick metrics summary */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="text-sm font-semibold text-gray-900 mb-2">Roll Facts</div>

        {/* Roll Facts */}
        {(() => {
          // Bucket helpers (W/R/O)
          const getBucket = (base, t) => toNum(getMetricValue({ code: `${base}_${t}`, type: "money" }, "assessor"));

          const buckets = [
            { key: "w", label: "W" },
            { key: "r", label: "R" },
            { key: "o", label: "O" },
          ];

          const rows = buckets.map((b) => {
            const mc = getBucket("market_current_year", b.key);
            const mp = getBucket("market_prior_year", b.key);
            const tc = getBucket("taxable_current_year", b.key);
            const tp = getBucket("taxable_prior_year", b.key);
            return { ...b, mc, mp, tc, tp };
          });

          const hasAnyBucketValue = rows.some((r) => r.mc !== null || r.mp !== null || r.tc !== null || r.tp !== null);

          const sum = (vals) => {
            let s = 0;
            let has = false;
            for (const v of vals) {
              if (v === null || v === undefined) continue;
              s += Number(v);
              has = true;
            }
            return has ? s : null;
          };

          if (!hasAnyBucketValue) {
            // Legacy fallback (older imports)
            return (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <MoneyTriplet label="Market (Current Year)" code="market_current_year" />
                <MoneyTriplet label="Market (Prior Year)" code="market_prior_year" />
                <MoneyTriplet label="Taxable (Current Year)" code="taxable_current_year" />
                <MoneyTriplet label="Taxable (Prior Year)" code="taxable_prior_year" />
              </div>
            );
          }

          const total = {
            mc: sum(rows.map((r) => r.mc)),
            mp: sum(rows.map((r) => r.mp)),
            tc: sum(rows.map((r) => r.tc)),
            tp: sum(rows.map((r) => r.tp)),
          };

          return (
            <div className="overflow-auto">
              <table className="min-w-[760px] w-full text-sm">
                <thead className="text-left text-gray-600">
                  <tr className="border-b">
                    <th className="py-2 pr-3">Interest</th>
                    <th className="py-2 px-3 text-right">Market (CY)</th>
                    <th className="py-2 px-3 text-right">Taxable (CY)</th>
                    <th className="py-2 px-3 text-right">Market (PY)</th>
                    <th className="py-2 pl-3 text-right">Taxable (PY)</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key} className="border-t">
                      <td className="py-2 pr-3 font-semibold text-gray-900">{r.label}</td>
                      <td className="py-2 px-3 text-right">${fmtMoney0(r.mc)}</td>
                      <td className="py-2 px-3 text-right">${fmtMoney0(r.tc)}</td>
                      <td className="py-2 px-3 text-right">${fmtMoney0(r.mp)}</td>
                      <td className="py-2 pl-3 text-right">${fmtMoney0(r.tp)}</td>
                    </tr>
                  ))}

                  <tr className="border-t bg-gray-50">
                    <td className="py-2 pr-3 font-semibold text-gray-900">Total</td>
                    <td className="py-2 px-3 text-right font-semibold">${fmtMoney0(total.mc)}</td>
                    <td className="py-2 px-3 text-right font-semibold">${fmtMoney0(total.tc)}</td>
                    <td className="py-2 px-3 text-right font-semibold">${fmtMoney0(total.mp)}</td>
                    <td className="py-2 pl-3 text-right font-semibold">${fmtMoney0(total.tp)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}

		
		<div className="mt-3 rounded-xl border border-gray-100 bg-white p-3">
		  <div className="text-xs text-gray-500">Taxing Jurisdictions</div>
		  <div className="text-sm text-gray-900 whitespace-pre-wrap">
			{(
			  getMetricValue({ code: "taxing_jurisdictions", type: "text" }, "ba") ||
			  getMetricValue({ code: "taxing_jurisdictions", type: "text" }, "assessor") ||
			  "—"
			)}
		  </div>
		</div>
		
		<div className="mt-3 rounded-xl border border-gray-100 bg-white p-3">
		  <div className="text-xs text-gray-500">Legal Description</div>
		  <div className="text-sm text-gray-900 whitespace-pre-wrap">
			{(
			  getMetricValue({ code: "legal_description", type: "text" }, "ba") ||
			  getMetricValue({ code: "legal_description", type: "text" }, "assessor") ||
			  "—"
			)}
		  </div>
		</div>
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
      <div className="text-xs text-gray-500">{k}</div>
      <div className="text-sm font-semibold text-gray-900 break-words">{String(v ?? "—")}</div>
    </div>
  );
}

function CategoryEditor({
  title,
  description = "",
  metrics,
  onSave,
  getMetricValue,
  getComparable3PStartRate,
  showHiddenMetrics = false,
  showThirdParty = false,
}) {
  const isExpenses = String(title || "").toLowerCase().includes("expenses");

  const ALWAYS_SHOW = new Set([
    // Core workflow levers / important drivers
    "loe_comparable",
    "loe_basis",
    "loe",
    "oil_price",
    "gas_price",

    // ✅ Comparable start rates (3P computed shows inside these rows)
    "comparable_oil_start_rate",
    "comparable_gas_start_rate",

    "oil_decline_rate",
    "gas_decline_rate",
    "discount_rate",
    "economic_life_years",
    "net_equipment_value",
    "working_interest_pct",
    "royalty_interest_pct",
  ]);

  function isBlankValue(v) {
    if (v === null || v === undefined) return true;
    const s = String(v).trim();
    return s === "" || s === "—";
  }

  function metricIsBlankEverywhere(m) {
    if (!m?.code) return true;
    if (ALWAYS_SHOW.has(m.code)) return false;

    // Comparable LOE should always show (computed/override row)
    if (m.code === "loe_comparable") return false;

    const a = getMetricValue(m, "assessor");
    const act = getMetricValue(m, "actual");
    const ba = getMetricValue(m, "ba");

    // IMPORTANT: numeric "0" is not blank.
    const aBlank = isBlankValue(a);
    const actBlank = isBlankValue(act);
    const baBlank = isBlankValue(ba);

    return aBlank && actBlank && baBlank;
  }

  const visibleMetrics = (metrics || []).filter((m) => {
    if (showHiddenMetrics) return true;
    return !metricIsBlankEverywhere(m);
  });

  const scenarios = showThirdParty
    ? SCENARIOS_BASE
    : SCENARIOS_BASE.filter((s) => s.key !== "third_party");

  function toNumLocal(x) {
    if (x === null || x === undefined) return null;
    const s = String(x).trim();
    if (!s) return null;
    const n = Number(s.replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
  }

  function fmtMoney(v) {
    const n = toNumLocal(v);
    if (n === null) return "—";
    try {
      return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
    } catch {
      return String(n);
    }
  }

  function getBasis(scenarioKey) {
    const raw = getMetricValue({ code: "loe_basis", type: "text" }, scenarioKey);
    return String(raw || "").trim(); // "gross" | "net_of_marketing" | ""
  }

  // ✅ Old behavior:
  // - Assessor Comparable LOE = imported LOE (metric_code "loe")
  // - Actual Comparable LOE = derived from buckets (opex + marketing depending on assessor basis)
  // - BA Comparable LOE = defaults to assessor comparable, but can be overridden via loe_comparable (BA)
  function computedComparableLOE(scenarioKey) {
    const assessorComparable = toNumLocal(getMetricValue({ code: "loe", type: "money" }, "assessor"));

    if (scenarioKey === "assessor") return assessorComparable ?? 0;

    // BA defaults to assessor comparable (override handled separately)
    if (scenarioKey === "ba") return assessorComparable ?? 0;

    // Actual uses buckets and always follows assessor basis
    const opex = toNumLocal(getMetricValue({ code: "opex", type: "money" }, "actual")) || 0;
    const mkt = toNumLocal(getMetricValue({ code: "marketing", type: "money" }, "actual")) || 0;

    const basis = getBasis("assessor");
    const isNet = basis === "net_of_marketing";
    return isNet ? opex : opex + mkt;
  }

  // ✅ Resolver used for display (BA override wins)
  function resolvedComparableLOE(scenarioKey) {
    if (scenarioKey !== "ba") return computedComparableLOE(scenarioKey);

    const overrideRaw = getMetricValue({ code: "loe_comparable", type: "money" }, "ba");
    const overrideNum = toNumLocal(overrideRaw);
    if (overrideNum !== null) return overrideNum;

    return computedComparableLOE("ba");
  }

  // Lock rules
  function isLocked(m, scenarioKey) {
    // LOE basis mirrors assessor for Actual + BA
    if (m?.code === "loe_basis" && (scenarioKey === "actual" || scenarioKey === "ba")) return true;

    // BA no longer uses buckets
    if (isExpenses && scenarioKey === "ba" && ["opex", "marketing", "ad_valorem"].includes(m?.code)) {
      return true;
    }

    // In expenses tab, lock assessor input for everything except LOE basis (same as before)
    if (isExpenses && scenarioKey === "assessor" && m?.code !== "loe_basis") return true;

    return false;
  }

  function canEdit(m, scenarioKey) {
    const allowed = Array.isArray(m?.editableScenarios) ? m.editableScenarios : [];

    // Treat 3rd Party like Actual for edit-permissions
    if (scenarioKey === "third_party") {
      return allowed.includes("third_party") || allowed.includes("actual");
    }

    return allowed.includes(scenarioKey);
  }

  const totalCount = (metrics || []).length;
  const shownCount = (visibleMetrics || []).length;
  const hiddenCount = Math.max(0, totalCount - shownCount);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b bg-gray-50">
        <div className="text-sm font-semibold text-gray-900">{title}</div>
        {description ? <div className="text-xs text-gray-500 mt-1">{description}</div> : null}
        <div className="text-xs text-gray-500 mt-1">
          {shownCount} metrics{!showHiddenMetrics && hiddenCount > 0 ? ` (hidden: ${hiddenCount})` : ""}
        </div>
      </div>

      <div className="overflow-auto">
        <table className="min-w-[980px] w-full text-sm">
          <thead className="bg-white text-gray-600 text-left">
		    <tr className="border-b">
			  <th className="py-2 px-3">Metric</th>

			  {scenarios.map((s) => (
			    <th key={s.key} className="py-2 px-3">
				  {s.label}
			    </th>
			  ))}

			  <th className="py-2 px-3">Unit</th>
		    </tr>
		  </thead>

          <tbody>
            {(visibleMetrics || []).map((m) => {
              return (
                <tr key={m.code} className="border-t align-top">
                  <td className="py-2 px-3">
                    <div className="font-semibold text-gray-900">{m.label || m.code}</div>
                    <div className="text-xs text-gray-500 font-mono">{m.code}</div>
                  </td>

                  {scenarios.map((s) => {
                    // ✅ Comparable LOE row restored
                    if (m?.code === "loe_comparable") {
                      const computedBA = computedComparableLOE("ba");

                      if (s.key !== "ba") {
                        return (
                          <td key={s.key} className="py-2 px-3">
                            <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-right font-semibold">
                              {fmtMoney(resolvedComparableLOE(s.key))}
                            </div>
                          </td>
                        );
                      }

                      // BA override state
                      const overrideRaw = getMetricValue({ code: "loe_comparable", type: "money" }, "ba");
                      const overrideNum = toNumLocal(overrideRaw);
                      const isOverride = overrideNum !== null;

                      return (
                        <td key={s.key} className="py-2 px-3">
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <span
                                className={[
                                  "text-[11px] px-2 py-0.5 rounded-full border",
                                  isOverride
                                    ? "border-amber-200 bg-amber-50 text-amber-800"
                                    : "border-gray-200 bg-gray-50 text-gray-600",
                                ].join(" ")}
                              >
                                {isOverride ? "OVERRIDE" : "CALC"}
                              </span>

                              {isOverride ? (
                                <button
                                  type="button"
                                  className="text-xs text-blue-600 hover:underline"
                                  onClick={() =>
                                    onSave("ba", "loe_comparable", "", { code: "loe_comparable", type: "money", unit: "$" })
                                  }
                                  title="Clear override and return to calculated"
                                >
                                  Clear
                                </button>
                              ) : null}
                            </div>

                            <MetricInput
                              metric={{ ...m, type: "money" }}
                              scenarioKey="ba"
                              value={overrideRaw}
                              placeholder={isOverride ? "—" : fmtMoney(computedBA)}
                              onCommit={(nextVal) => onSave("ba", "loe_comparable", nextVal, { ...m, type: "money" })}
                            />

                            <div className="text-[11px] text-gray-400">
                              {isOverride ? "Computed (ignored)" : "Computed"}: {fmtMoney(computedBA)}
                            </div>
                          </div>
                        </td>
                      );
                    }
					
					// ✅ Comparable start rates: show computed 3P value + basis dropdown INSIDE the 3P column
                    if (
                      (m?.code === "comparable_oil_start_rate" || m?.code === "comparable_gas_start_rate") &&
                      s.key === "third_party"
                    ) {
                      const isOil = m.code === "comparable_oil_start_rate";
                      const basisCode = isOil ? "tp_start_rate_basis_oil" : "tp_start_rate_basis_gas";

                      // current basis (BA wins; then third_party; then default)
                      const currentBasis =
                        String(getMetricValue({ code: basisCode, type: "text" }, "ba") || "").trim() ||
                        String(getMetricValue({ code: basisCode, type: "text" }, "third_party") || "").trim() ||
                        "sep_dec";

                      const computed =
					    typeof getComparable3PStartRate === "function"
						  ? getComparable3PStartRate(isOil ? "oil" : "gas")
						  : 0;

                      return (
                        <td key={s.key} className="py-2 px-3">
                          <div className="space-y-1">
                            <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-right font-semibold">
                              {Number(computed || 0).toLocaleString()}
                            </div>

                            <select
                              className="w-full rounded-xl border border-gray-200 px-3 py-2 bg-white text-sm"
                              value={currentBasis}
                              onChange={(e) => {
                                const next = e.target.value;
                                // Save basis as BA fact so it works from Detail OR Bulk Analysis
                                onSave(
                                  "ba",
                                  basisCode,
                                  next,
                                  { code: basisCode, type: "text", unit: null, editableScenarios: ["ba"] }
                                );
                              }}
                              title="3P Start Rate Basis"
                            >
                              <option value="sep_dec">Sep–Dec Avg</option>
                              <option value="last_1">Last Month</option>
                              <option value="last_3">Last 3 Mo Avg</option>
                              <option value="last_4">Last 4 Mo Avg</option>
                              <option value="last_6">Last 6 Mo Avg</option>
                              <option value="last_12">Last 12 Mo Avg</option>
                            </select>
                          </div>
                        </td>
                      );
                    }

                    // Normal metrics
                    const locked = isLocked(m, s.key);
                    const editable = canEdit(m, s.key) && !locked;

                    return (
                      <td key={s.key} className="py-2 px-3">
                        {locked ? (
                          m?.code === "loe_basis" && (s.key === "actual" || s.key === "ba") ? (
                            <MetricInput
                              metric={m}
                              scenarioKey={s.key}
                              value={getMetricValue(m, s.key)}
                              disabled={true}
                              onCommit={() => {}}
                            />
                          ) : (
                            <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-gray-500">
                              —
                            </div>
                          )
                        ) : (
                          <MetricInput
                            metric={m}
                            scenarioKey={s.key}
                            value={getMetricValue(m, s.key)}
                            disabled={!editable}
                            onCommit={(nextVal) => {
                              if (!editable) return;
                              onSave(s.key, m.code, nextVal, m);
                            }}
                          />
                        )}
                      </td>
                    );
                  })}

                  <td className="py-2 px-3">{m.unit || "—"}</td>
                </tr>
              );
            })}

            {(visibleMetrics || []).length === 0 && (
              <tr>
                <td className="py-3 px-3 text-gray-500" colSpan={scenarios.length + 2}>
                  No metrics in this group.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-2 text-xs text-gray-500 border-t">Tip: edit a cell and tab/click out to save (blur-save).</div>
    </div>
  );
}

// Same MetricInput behavior you had before (supports LOE basis dropdown, otherwise text input w/ blur-save)
function MetricInput({ metric, scenarioKey, value, onCommit, placeholder = "—", disabled = false }) {
  const code = metric?.code || "";
  const [local, setLocal] = useState("");

  useEffect(() => {
    const v = value === null || value === undefined ? "" : String(value);
    setLocal(v);
  }, [value, scenarioKey, metric?.code]);

  if (code === "loe_basis") {
    const normalized = String(local || "").trim();
    return (
      <select
        className={[
          "w-full rounded-xl border border-gray-200 px-3 py-2",
          disabled ? "bg-gray-50 text-gray-500 cursor-not-allowed" : "bg-white",
        ].join(" ")}
        disabled={disabled}
        value={normalized}
        onChange={(e) => {
          const next = e.target.value;
          setLocal(next);
          if (!disabled) onCommit(next);
        }}
      >
        <option value="">—</option>
        <option value="gross">Gross</option>
        <option value="net_of_marketing">Net of Marketing</option>
      </select>
    );
  }

  return (
    <input
      className={[
        "w-full rounded-xl border border-gray-200 px-3 py-2",
        disabled ? "bg-gray-50 text-gray-500 cursor-not-allowed" : "bg-white",
      ].join(" ")}
      value={local}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        if (disabled) return;
        onCommit(local);
      }}
    />
  );
}
